'use es6';

import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import UIErrorMessage from 'UIComponents/error/UIErrorMessage';
import UIButton from 'UIComponents/button/UIButton';
import UIAlert from 'UIComponents/alert/UIAlert';
import UIIllustration from 'UIComponents/image/UIIllustration';
import H5 from 'UIComponents/elements/headings/H5';
import I18n from 'I18n';
import text from 'I18n/utils/unescapedText';
import UILink from 'UIComponents/link/UILink';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { getCallingLimits } from 'calling-internal-common/utils/getCallingLimits';
import { createTicket } from 'calling-lifecycle-internal/zorse/zorseControls';
import { getAsyncAccountVerificationCanAppeal, getHasAppealed, getIsAppealStateRejected } from '../operators/callingAccessGetters';
import PortalIdParser from 'PortalIdParser';
import { CommunicatorLogger } from 'calling-lifecycle-usage-tracker/callingTracker';
import { logPageAction } from 'calling-error-reporting/report/error';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const getTranslationKey = label => {
  return `customerDataCommunicator.Communicator.permissions.${label}`;
};
class CallingSuspensionMessage extends PureComponent {
  constructor(props) {
    super();
    this.handleOpenSupportWindow = () => {
      createTicket();
    };
    const callingLimit = getCallingLimits(props.scopes);
    // TODO: recreate CallingSalesPro without global scopes containers.
    this.isCallingFree = callingLimit.type === 'free';
    this.setOpenAppealForm = this.setOpenAppealForm.bind(this);
  }
  setOpenAppealForm() {
    logPageAction({
      key: 'CALLING_APPEAL_MODAL_OPEN',
      tags: {
        subType: 'calling-access'
      }
    });
    CommunicatorLogger.log('callingInteraction', {
      action: 'Click Appeal',
      activity: 'call',
      channel: 'outbound call',
      source: this.props.appIdentifier
    });
    window.location.href = `/account-verification/${PortalIdParser.get()}?source=CALLING`;
  }
  renderAlert() {
    return /*#__PURE__*/_jsx(UIAlert, {
      className: "m-y-3",
      style: {
        width: '97%',
        marginLeft: 'auto',
        marginRight: 'auto'
      },
      titleText: I18n.text(getTranslationKey('alreadyAppealTitle')),
      type: "tip",
      children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: getTranslationKey('alreadyAppealText')
      })
    });
  }
  renderAppealAction() {
    return /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: getTranslationKey('message')
      }), /*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx(UIButton, {
          className: "m-y-6",
          onClick: this.setOpenAppealForm,
          use: 'tertiary',
          children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: getTranslationKey('appealButton')
          })
        })
      })]
    });
  }
  renderRejected() {
    return /*#__PURE__*/_jsx("div", {
      children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: getTranslationKey('rejectedText')
      })
    });
  }
  renderAppealState() {
    const {
      callingAccess
    } = this.props;
    const alreadyAppealed = getHasAppealed(callingAccess);
    const isRejected = getIsAppealStateRejected(callingAccess);
    if (isRejected) {
      return this.renderRejected();
    } else if (!alreadyAppealed) {
      return this.renderAppealAction();
    }
    return null;
  }
  renderNoAppeal() {
    let langKey = 'callingPaid';
    let deactivated_options = {
      onClick: this.handleOpenSupportWindow
    };
    if (this.isCallingFree) {
      langKey = 'callingFree';
      deactivated_options = {
        href: 'https://knowledge.hubspot.com/account-settings/account-suspension-faq',
        target: '_blank'
      };
    }
    const deactivated_lang = `twilio.notEnabled.error.deactivated.info.${langKey}_jsx`;
    return /*#__PURE__*/_jsx("div", {
      className: "flex-column justify-center p-all-4",
      style: {
        flex: 1,
        maxWidth: '520px',
        margin: '0 auto'
      },
      children: /*#__PURE__*/_jsx(UIErrorMessage, {
        illustration: "keys",
        illustrationProps: {
          height: 100
        },
        title: text('twilio.notEnabled.error.deactivated.title'),
        children: /*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: deactivated_lang,
            options: deactivated_options,
            elements: {
              UILink
            }
          })
        })
      })
    });
  }
  renderFullWidth() {
    const {
      callingAccess
    } = this.props;
    const alreadyAppealed = getHasAppealed(callingAccess);
    const canAppeal = getAsyncAccountVerificationCanAppeal(callingAccess);
    const isRejected = getIsAppealStateRejected(callingAccess);
    if (!canAppeal && !alreadyAppealed) {
      return this.renderNoAppeal();
    }
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [alreadyAppealed && !isRejected && this.renderAlert(), /*#__PURE__*/_jsx("div", {
        className: "flex-column justify-center p-all-4",
        style: {
          flex: 1,
          maxWidth: '520px',
          margin: '0 auto'
        },
        children: /*#__PURE__*/_jsx(UIErrorMessage, {
          illustration: "lock",
          illustrationProps: {
            width: '25%'
          },
          style: {
            width: '100%',
            margin: 0
          },
          title: I18n.text(getTranslationKey(isRejected ? 'rejectedTitle' : 'title')),
          children: this.renderAppealState()
        })
      })]
    });
  }
  renderPopoverView() {
    const {
      callingAccess
    } = this.props;
    const alreadyAppealed = getHasAppealed(callingAccess);
    const canAppeal = getAsyncAccountVerificationCanAppeal(callingAccess);
    const isRejected = getIsAppealStateRejected(callingAccess);
    if (!canAppeal && !alreadyAppealed) {
      return this.renderNoAppeal();
    }
    return /*#__PURE__*/_jsxs("div", {
      className: 'flex-column justify-start p-all-4',
      children: [alreadyAppealed && !isRejected && this.renderAlert(), /*#__PURE__*/_jsxs("div", {
        className: 'justify-start m-y-3',
        children: [/*#__PURE__*/_jsx("div", {
          children: /*#__PURE__*/_jsx(UIIllustration, {
            name: "lock",
            width: alreadyAppealed && !isRejected ? 100 : 250
          })
        }), /*#__PURE__*/_jsxs("div", {
          className: 'text-left m-left-4',
          style: {
            alignSelf: 'center'
          },
          children: [/*#__PURE__*/_jsx(H5, {
            className: "m-bottom-2",
            children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
              message: getTranslationKey(isRejected ? 'rejectedTitle' : 'title')
            })
          }), this.renderAppealState()]
        })]
      })]
    });
  }
  render() {
    if (this.props.useFullWidth) {
      return this.renderFullWidth();
    } else {
      return this.renderPopoverView();
    }
  }
}
CallingSuspensionMessage.propTypes = {
  callingAccess: PropTypes.object.isRequired,
  useFullWidth: PropTypes.bool,
  scopes: PropTypes.object.isRequired,
  appIdentifier: PropTypes.string.isRequired
};
export default CallingSuspensionMessage;